/* Owns the authenticated layout of PANEL.md §4: Sidebar + Topbar + StatusStrip +
   <Outlet/> + CommandPalette + ToastViewport, plus the two things that must
   happen exactly once per tab — opening the WebSocket after the session
   bootstrap, and installing the global keyboard shortcuts of §13. */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router'

import { useLang, useT } from '../i18n/index.js'
import { onAccessTokenRefreshed } from '../lib/api.js'
import { useAuthState } from '../lib/auth-store.js'
import { useRole } from '../lib/permissions.js'
import { wsClient } from '../lib/ws.js'
import { useServerFeed } from '../ws/useServerFeed.js'
import { CommandPalette } from './CommandPalette.js'
import { Sidebar } from './Sidebar.js'
import { StatusStrip } from './StatusStrip.js'
import { Topbar } from './Topbar.js'

/** `g` then one of these jumps to that page of the active server (PANEL.md §13). */
const GOTO_KEYS: Readonly<Record<string, string>> = {
  d: 'dashboard',
  l: 'live',
  p: 'players',
  b: 'bans',
  c: 'config',
}

/** How long a pressed `g` stays armed before it goes back to being an ordinary key. */
const CHORD_WINDOW_MS = 1000

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

export function AppShell(): ReactNode {
  const t = useT()
  const { lang } = useLang()
  const navigate = useNavigate()
  const params = useParams()
  const { status } = useAuthState()
  // `useParams` resolves against the deepest match, so the shell sees the id of
  // whatever `/s/:serverId/*` route is mounted beneath it without duplicating
  // ServerLayout's effect ordering.
  const serverId = params['serverId'] ?? null
  const role = useRole(serverId)

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const chordArmedAt = useRef(0)

  useServerFeed(lang)

  useEffect(() => {
    if (status !== 'authenticated') return
    wsClient.connect()
    // A proactive refresh re-authenticates the open socket instead of tearing it
    // down, so the feed never gaps at the 15-minute token boundary.
    const off = onAccessTokenRefreshed((token) => wsClient.sendAuth(token))
    return () => {
      off()
      wsClient.close()
    }
  }, [status])

  const openPalette = useCallback(() => setPaletteOpen(true), [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen(true)
        return
      }
      // Everything below is a bare key, so it must never fire while the user is
      // typing — including inside the palette's own search field.
      if (event.ctrlKey || event.metaKey || event.altKey || isTypingTarget(event.target)) return

      if (event.key === '/') {
        const search = document.querySelector<HTMLInputElement>('main input[type="search"]')
        if (search) {
          event.preventDefault()
          search.focus()
        }
        return
      }

      const now = Date.now()
      if (event.key === 'g') {
        chordArmedAt.current = now
        return
      }
      if (now - chordArmedAt.current > CHORD_WINDOW_MS) return
      const target = GOTO_KEYS[event.key]
      if (!target || !serverId) return
      chordArmedAt.current = 0
      event.preventDefault()
      void navigate(`/s/${serverId}/${target}`)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [navigate, serverId])

  return (
    <div className="flex min-h-screen bg-surface text-text">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-md focus:bg-elevated focus:px-3 focus:py-2 focus:text-text"
      >
        {t('common.skipToContent')}
      </a>

      <Sidebar serverId={serverId} role={role} mobileOpen={sidebarOpen} onMobileOpenChange={setSidebarOpen} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenSidebar={() => setSidebarOpen(true)} onOpenPalette={openPalette} />
        {serverId ? <StatusStrip serverId={serverId} /> : null}
        <main id="main" className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-4 lg:px-6 lg:py-6">
          <Outlet />
        </main>
      </div>

      {/* `ToastViewport` is mounted once in `main.tsx`, above the router: it is
          fixed to the viewport corner, and the public auth pages raise toasts
          too, so a second instance here would only double every message. */}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  )
}
